import { ArrowLeft, Search, X } from 'lucide-react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { Ref, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { useProductsStore } from '../../state/useProductsStore';
import routes from '../../config/routes';

interface SearchInputProps {
  ref?: Ref<HTMLInputElement>;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  autoFocus?: boolean;
  onBack?: () => void;
}

const SearchInput = ({ ref, value, onChange, onFocus, autoFocus, onBack }: SearchInputProps) => {
  const { t } = useTranslation();

  return (
    <div className="relative min-w-0 flex-1">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label={t('products.filters.search-by.cancel') as string}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={16} />
        </button>
      ) : (
        <Search
          aria-hidden="true"
          size={16}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
      )}
      <Input
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        autoFocus={autoFocus}
        placeholder={t('products.filters.search-by.label') as string}
        className="h-9 border-transparent bg-card pl-9 pr-9 text-card-foreground shadow-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-white/60 dark:bg-card lg:h-10"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Limpiar búsqueda"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const SearchBar = () => {
  const [active, setActive] = useState(false);
  const overlayInputRef = useRef<HTMLInputElement>(null);
  const searchBy = useProductsStore(s => s.filters.searchBy);
  const setSearchBy = useProductsStore(s => s.setSearchBy);
  const navigate = useNavigate();
  const location = useLocation();
  const handleChange = useCallback(
    (value: string) => {
      setSearchBy(value);
      if (value && location.pathname !== routes.publicRoutes.products.path) {
        navigate(routes.publicRoutes.products.path);
      }
    },
    [setSearchBy, navigate, location.pathname],
  );
  const reducedMotion = useReducedMotion();
  const duration = reducedMotion ? 0 : 0.2;

  const handleCancel = () => {
    overlayInputRef.current?.blur();
    setActive(false);
  };

  return (
    <div className="relative w-full">
      <SearchInput value={searchBy} onChange={handleChange} onFocus={() => setActive(true)} />
      {/* Mobile takeover: focusing the search input expands it to fill the
          whole header bar (width + height), matching the Mercado Libre pattern.
          A full-screen backdrop behind it closes the search (same as the back
          arrow) when the user taps anywhere else. */}
      <AnimatePresence>
        {active && (
          <>
            <m.div
              aria-hidden="true"
              onClick={handleCancel}
              className="fixed inset-0 z-[59] lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration }}
            />
            <m.div
              className="fixed inset-x-0 top-0 z-[60] flex h-16 items-center bg-primary px-4 lg:hidden"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration }}
            >
              <SearchInput
                ref={overlayInputRef}
                value={searchBy}
                onChange={handleChange}
                autoFocus
                onBack={handleCancel}
              />
            </m.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;
